import { ContractPromise } from '@polkadot/api-contract';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BigNumber } from 'bignumber.js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { parseUnits } from 'viem';
import {
  useConfig,
  useReadContract,
  useSwitchChain,
  useAccount as useWagmiAccount,
  useWriteContract,
} from 'wagmi';
import { readContractQueryOptions } from 'wagmi/query';
import { D9_USDT_ABI, EVM_USDT_ABI, TRON_USDT_ABI } from '@/lib/constants';
import { getD9Api, getGasLimits } from '@/lib/d9-api';
import { clientEnv } from '@/lib/env/client-env';
import { parseD9Address } from '@/lib/utils/d9';
import { currentChain } from '@/lib/wagmi';
import { useD9Address } from './use-d9';
import { useTronAddress } from './use-tron';

export function useEvmUsdtApproval() {
  const { t } = useTranslation();
  const { isConnected, address, chain } = useWagmiAccount();
  const { switchChainAsync } = useSwitchChain();
  const config = useConfig();
  const { writeContractAsync } = useWriteContract();
  const { data: decimals, refetch: refetchDecimals } = useReadContract({
    address: clientEnv.VITE_BSC_USDT_CONTRACT_ADDRESS as `0x${string}`,
    abi: EVM_USDT_ABI,
    functionName: 'decimals',
    query: {
      enabled: isConnected,
    },
  });
  const queryClient = useQueryClient();

  return {
    approve: useMutation({
      mutationFn: async (params: {
        amount: string;
        contractAddress: string;
      }) => {
        if (!address) throw new Error(t('errors.walletNotConnected'));
        if (chain?.id !== currentChain.id) {
          await switchChainAsync({ chainId: currentChain.id });
        }
        const allowance = await queryClient.fetchQuery(
          readContractQueryOptions(config, {
            address: clientEnv.VITE_BSC_USDT_CONTRACT_ADDRESS as `0x${string}`,
            abi: EVM_USDT_ABI,
            functionName: 'allowance',
            args: [address, params.contractAddress as `0x${string}`],
          }),
        );
        const _decimals = decimals ?? (await refetchDecimals()).data;
        if (!_decimals) throw new Error(t('errors.decimalsNotFound'));

        const targetAllowance = parseUnits(params.amount, Number(_decimals));

        // const supply = await queryClient.fetchQuery(
        //   readContractQueryOptions(config, {
        //     address: clientEnv.VITE_BSC_USDT_CONTRACT_ADDRESS as `0x${string}`,
        //     abi: EVM_USDT_ABI,
        //     functionName: 'totalSupply',
        //   }),
        // );

        console.info(
          'allowance',
          allowance.toString(),
          'targetAllowance',
          targetAllowance.toString(),
          'decimals',
          _decimals,
        );

        if (allowance < targetAllowance) {
          await writeContractAsync({
            address: clientEnv.VITE_BSC_USDT_CONTRACT_ADDRESS as `0x${string}`,
            abi: EVM_USDT_ABI,
            functionName: 'approve',
            args: [params.contractAddress as `0x${string}`, targetAllowance],
          });
        }
      },
    }),
  };
}

export function useTronUsdtApproval() {
  const { t } = useTranslation();
  const tronAddress = useTronAddress();

  const contract = useMemo(
    () =>
      window.tronWeb?.contract(
        TRON_USDT_ABI,
        clientEnv.VITE_TRON_USDT_CONTRACT_ADDRESS,
      ),
    [],
  );

  return {
    approve: useMutation({
      mutationFn: async (params: {
        amount: string;
        contractAddress: string;
      }) => {
        if (!contract) throw new Error(t('errors.contractNotFound'));
        if (!tronAddress) throw new Error(t('errors.walletNotConnected'));

        const decimals = await contract['decimals']().call();
        const currentAllowance = await contract['allowance'](
          tronAddress,
          params.contractAddress,
        ).call();

        // const supply = await contract['totalSupply']().call();

        const targetAllowanceBn = BigNumber(params.amount).multipliedBy(
          BigNumber(10).pow(decimals),
        );
        const allowanceBn = BigNumber(currentAllowance[0].toString());

        console.info(
          'target allowance bn',
          targetAllowanceBn.toString(),
          'allowance bn',
          allowanceBn.toString(),
          'decimals',
          decimals,
        );

        if (allowanceBn.isLessThan(targetAllowanceBn)) {
          await contract
            .approve(
              params.contractAddress,
              BigInt(targetAllowanceBn.toString()),
            )
            .send({
              callValue: 0,
              feeLimit: 100_000_000,
            });
        }
      },
    }),
  };
}

export function useD9Approval() {
  const { t } = useTranslation();
  const d9Address = useD9Address();

  return {
    approve: useMutation({
      mutationFn: async (params: {
        amount: string;
        contractAddress: string;
      }) => {
        if (!d9Address) throw new Error(t('errors.walletNotConnected'));
        const normalizedAddress = parseD9Address(d9Address);
        const d9Api = await getD9Api();

        const contract = new ContractPromise(
          d9Api,
          D9_USDT_ABI,
          clientEnv.VITE_D9_USDT_CONTRACT_ADDRESS,
        );

        let allowanceBn = BigNumber(0);
        let decimals = 2;

        const allowance = await contract.query['psp22::allowance']!(
          normalizedAddress,
          { gasLimit: getGasLimits(d9Api).readLimit },
          normalizedAddress,
          params.contractAddress,
        );
        if (allowance && allowance.result.isOk && allowance.output) {
          allowanceBn = BigNumber((allowance.output as any).asOk.toString());
        }

        const amountBn = BigNumber(params.amount).multipliedBy(
          BigNumber(10).pow(decimals),
        );

        if (allowanceBn.isLessThan(amountBn)) {
          return new Promise((resolve, reject) => {
            window.d9d.exec(
              'onD9ContractExec',
              {
                type: 'approveUsdtAllowance',
                params: {
                  address: `Dn${params.contractAddress}`,
                  // amount: BigNumber(params.amount)
                  //   .multipliedBy(BigNumber(10).pow(decimals))
                  //   .toFixed(0),
                  // amount: Math.ceil(Number(params.amount)),
                  amount: params.amount,
                },
              },
              (data) => {
                resolve(data);
              },
              (error) => {
                // alert(`${JSON.stringify(error)}`);
                reject(
                  new Error(
                    typeof error.msg === 'string' ? error.msg : 'Unknown error',
                  ),
                );
              },
            );
          });
        }
      },
    }),
  };
}
